/* eslint-disable max-classes-per-file */

/* iceberg-ui */
import { Box } from '@auviknetworks/iceberg-ui';

/*  MUI  */
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { IconButton, TableCell, TableRow, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

/* Shared */
import { ApplicationNameLinkCell } from 'application/Shared/Components/ApplicationNameLinkCell';
import { ManagedTable } from 'application/Shared/Components/Table';
import { Route, hmsFormatter } from 'application/Shared/Helpers';

/* Objects */
import { Application } from 'objects/Application';

/* React */
import React, { Component, useMemo } from 'react';
import { withTranslation } from 'react-i18next';

/* Charts */
import {
    Bar,
    BarChart,
    CartesianGrid,
    Legend,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';
import { withRouter } from 'router/withRouter';

const PartnerApplicationOverviewReports = (props) => {
    const { renderingScheduledReport, reportData, reportSetup, tab } = props;
    const {
        partnerApplicationOverviewApplicationsUsedBreakdown,
        partnerApplicationOverviewApplicationsUsedOverviewBreakdown,
        partnerApplicationOverviewApplicationsUsedApplicationBreakdown,
        partnerApplicationOverviewApplicationUsageBreakdown,
        partnerApplicationOverviewApplicationUsageOverviewBreakdown,
        partnerApplicationOverviewApplicationUsageApplicationBreakdown
    } = reportData || {};

    return (
        <Box width="100%">
            {tab === 'ApplicationsUsed' &&
                partnerApplicationOverviewApplicationsUsedBreakdown && (
                    <Box id="partnerApplicationOverviewApplicationsUsedBreakdown">
                        <Overview
                            {...props}
                            renderingScheduledReport={renderingScheduledReport}
                            reportSetup={reportSetup}
                            reportData={
                                partnerApplicationOverviewApplicationsUsedBreakdown
                            }
                        />
                    </Box>
                )}
            {tab === 'ApplicationsUsed' &&
                partnerApplicationOverviewApplicationsUsedOverviewBreakdown && (
                    <Box id="partnerApplicationOverviewApplicationsUsedOverviewBreakdown">
                        <OverviewBreakdown
                            {...props}
                            renderingScheduledReport={renderingScheduledReport}
                            reportSetup={reportSetup}
                            reportData={
                                partnerApplicationOverviewApplicationsUsedOverviewBreakdown
                            }
                        />
                    </Box>
                )}
            {tab === 'ApplicationsUsed' &&
                partnerApplicationOverviewApplicationsUsedApplicationBreakdown && (
                    <Box id="partnerApplicationOverviewApplicationsUsedApplicationBreakdown">
                        <ApplicationBreakdown
                            {...props}
                            renderingScheduledReport={renderingScheduledReport}
                            reportSetup={reportSetup}
                            reportData={
                                partnerApplicationOverviewApplicationsUsedApplicationBreakdown
                            }
                        />
                    </Box>
                )}
            {tab === 'ApplicationUsage' &&
                partnerApplicationOverviewApplicationUsageBreakdown && (
                    <Box id="partnerApplicationOverviewApplicationUsageBreakdown">
                        <UsageOverview
                            {...props}
                            renderingScheduledReport={renderingScheduledReport}
                            reportSetup={reportSetup}
                            reportData={
                                partnerApplicationOverviewApplicationUsageBreakdown
                            }
                        />
                    </Box>
                )}
            {tab === 'ApplicationUsage' &&
                partnerApplicationOverviewApplicationUsageOverviewBreakdown && (
                    <Box id="partnerApplicationOverviewApplicationUsageOverviewBreakdown">
                        <UsageOverviewBreakdown
                            {...props}
                            renderingScheduledReport={renderingScheduledReport}
                            reportSetup={reportSetup}
                            reportData={
                                partnerApplicationOverviewApplicationUsageOverviewBreakdown
                            }
                        />
                    </Box>
                )}
            {tab === 'ApplicationUsage' &&
                partnerApplicationOverviewApplicationUsageApplicationBreakdown && (
                    <Box id="partnerApplicationOverviewApplicationUsageApplicationBreakdown">
                        <ApplicationUsageBreakdown
                            {...props}
                            renderingScheduledReport={renderingScheduledReport}
                            reportSetup={reportSetup}
                            reportData={
                                partnerApplicationOverviewApplicationUsageApplicationBreakdown
                            }
                        />
                    </Box>
                )}
        </Box>
    );
};

const getUsageReportKeys = (data) => {
    const usageReportKeys = [];

    data.forEach((entry) => {
        usageReportKeys.push(entry.categoryName);
    });
    return usageReportKeys;
};

const getBarData = ({ data, variable }) => {
    const barData = [];
    data.months.forEach((month, index) => {
        const monthData = {};
        data.data.forEach((entry) => {
            monthData[entry.categoryName] =
                entry[parseInt(index, 10) + data.startingMonth][variable];
        });
        barData.push({
            ...monthData,
            name: month
        });
    });
    return barData;
};

const Overview = (props) => {
    const theme = useTheme();
    const { renderingScheduledReport, reportData, t } = props;

    const usageReportKeys = getUsageReportKeys(reportData.data);

    const barData = getBarData({
        data: reportData,
        variable: 'applicationCount'
    });

    return (
        <Box
            mt="8px"
            minHeight="500px"
            width="100%"
            data-trackid="partnerAppOverviewReportBarChartGridItemWithRenderingScheduleReport"
        >
            <Box
                sx={{ height: '400px', width: '100%' }}
                data-trackid="partnerAppOverviewReportBarChartGridItemNoRenderingScheduleReport"
            >
                <Typography variant="h5">
                    {t(
                        'Reports.PartnerApplicationOverviewReport.application_usage_by_category_overview'
                    )}
                </Typography>
                <Typography paragraph>
                    {t(
                        'Reports.partnerApplicationOverviewReport.breakdown.description'
                    )}
                </Typography>
                <ResponsiveContainer height="100%" minHeight={400} width="100%">
                    <BarChart data={barData}>
                        <CartesianGrid
                            stroke={theme.palette.chart.axis}
                            strokeDasharray="3 3"
                        />
                        <XAxis dataKey="name" />
                        <YAxis tickFormatter={(value) => value} />
                        <Tooltip />
                        <Legend
                            wrapperStyle={{
                                bottom: 0,
                                left: 0,
                                width: '100%'
                            }}
                        />
                        {usageReportKeys.map((row, index) => (
                            <Bar
                                isAnimationActive={!renderingScheduledReport}
                                key={`iuo_${row}`}
                                dataKey={row}
                                stackId="a"
                                fill={
                                    theme.palette.graph.colors[
                                        index %
                                            theme.palette.graph.colors.length
                                    ]
                                }
                            />
                        ))}
                    </BarChart>
                </ResponsiveContainer>
            </Box>
        </Box>
    );
};

const headersOverviewBreakdown = (t) => [
    {
        align: 'left',
        field: 'riubCategory',
        renderHeader: <strong>{t('Common.category')}</strong>,
        width: '50%'
    },
    {
        align: 'right',
        field: 'riubUsers',
        renderHeader: <strong>{t('Common.users')}</strong>,
        width: '25%'
    },
    {
        align: 'right',
        field: 'riubPercentage',
        renderHeader: <strong>{t('Common.percentage')}</strong>,
        width: '25%'
    }
];

const OverviewBreakdown = (props) => {
    const { renderingScheduledReport, reportData, t } = props;

    const rows = useMemo(() => reportData.data, [reportData.data]);

    const tableHeadersOverviewBreakdown = useMemo(
        () => headersOverviewBreakdown(t),
        [t]
    );

    return (
        <Box mt="32px" width="100%">
            <Typography variant="h5">
                {t(
                    'Reports.PartnerApplicationOverviewReport.application_usage_by_category_breakdown'
                )}
            </Typography>
            <Typography paragraph>
                {t(
                    'Reports.partnerApplicationOverviewReport.overviewBreakdown.description'
                )}
            </Typography>
            <ManagedTable
                headers={tableHeadersOverviewBreakdown}
                renderBody={(row) => (
                    <>
                        <TableCell>{row.categoryName}</TableCell>
                        <TableCell align="right">
                            {row.applicationCount}
                        </TableCell>
                        <TableCell align="right">
                            {`${row.percentage} %`}
                        </TableCell>
                    </>
                )}
                renderBodyKey={null}
                renderFooter={!renderingScheduledReport}
                rows={rows}
                rowsPerPage={-1}
                searchEnabled={false}
                sortDirection="DESC"
                sortFieldType="number"
                sortKey="applicationCount"
            />
        </Box>
    );
};

class ApplicationBreakdown extends Component {
    constructor(props) {
        super(props);
        this.state = {
            data: []
        };
    }

    componentDidMount() {
        const { reportData } = this.props;
        this.setState({ data: reportData.data });
    }

    setOpen(row) {
        const { data } = this.state;

        this.setState({
            data: data.map((a) => ({
                ...a,
                open: a.applicationId === row.applicationId ? !a.open : a.open
            }))
        });
    }

    // eslint-disable-next-line class-methods-use-this
    headersApplicationBreakdown = ({ reportDataMonths, t }) => {
        const headers = [];
        headers.push({
            align: 'left',
            field: 'chevron',
            renderHeader: <strong>&nbsp;</strong>,
            width: '5%'
        });
        headers.push({
            align: 'left',
            field: 'application',
            renderHeader: <strong>{t('Common.application')}</strong>,
            width: '47%'
        });
        headers.push({
            align: 'left',
            field: 'overviewSubCategory',
            renderHeader: <strong>{t('Common.quarter')}</strong>,
            width: '12%'
        });
        headers.push({
            align: 'left',
            field: 'overviewRisk',
            renderHeader: <strong>{reportDataMonths[0]}</strong>,
            width: '12%'
        });
        headers.push({
            align: 'left',
            field: 'overviewBusinessOwner',
            renderHeader: <strong>{reportDataMonths[1]}</strong>,
            width: '12%'
        });
        headers.push({
            align: 'left',
            field: 'overviewStatus',
            renderHeader: <strong>{reportDataMonths[2]}</strong>,
            width: '12%'
        });
        return headers;
    };

    renderRow = (row) => {
        const { match, renderingScheduledReport, reportData } = this.props;

        const { partnerId } = match.params;
        const basePartnerRoute = Route.buildBasePartnerRoute({
            partnerId
        });

        return (
            <React.Fragment key={`usage_app__${row.applicationId}`}>
                <TableRow sx={{ backgroundColor: 'table.rowBackground' }}>
                    <Application
                        data={reportData.applicationMap[row.applicationId]}
                        nameLinkTo={`${basePartnerRoute}/applicationSettings`}
                    >
                        <TableCell>
                            {!renderingScheduledReport && (
                                <IconButton
                                    data-trackid={
                                        row.open
                                            ? 'reports_partnerApplicationOverview_appBreakdown_collapseRow'
                                            : 'reports_partnerApplicationOverview_appBreakdown_expandRow'
                                    }
                                    aria-label="expand row"
                                    size="small"
                                    onClick={() => {
                                        this.setOpen(row);
                                    }}
                                    sx={{ color: 'icon.textColor' }}
                                >
                                    {row.open ? (
                                        <KeyboardArrowUp />
                                    ) : (
                                        <KeyboardArrowDown />
                                    )}
                                </IconButton>
                            )}
                        </TableCell>
                        <TableCell>
                            <ApplicationNameLinkCell nameNoTruncate />
                        </TableCell>
                        <TableCell>{row.quarter}</TableCell>
                        <TableCell>{row[reportData.startingMonth]}</TableCell>
                        <TableCell>
                            {row[reportData.startingMonth + 1]}
                        </TableCell>
                        <TableCell>
                            {row[reportData.startingMonth + 2]}
                        </TableCell>
                    </Application>
                </TableRow>
                {row.open &&
                    Object.keys(row.clients).map((clientId) => (
                        <TableRow
                            key={`ubreakdown_${clientId}`}
                            sx={{
                                backgroundColor: 'table.subtableBackground'
                            }}
                        >
                            <TableCell>
                                <strong>&nbsp;</strong>
                            </TableCell>
                            <TableCell>
                                {reportData.tenantLinkMap[clientId].tenant.name}
                            </TableCell>
                            <TableCell>
                                {row.clients[clientId].quarter.userCount}
                            </TableCell>
                            <TableCell>
                                {
                                    row.clients[clientId][
                                        reportData.startingMonth
                                    ].userCount
                                }
                            </TableCell>
                            <TableCell>
                                {
                                    row.clients[clientId][
                                        reportData.startingMonth + 1
                                    ].userCount
                                }
                            </TableCell>
                            <TableCell>
                                {
                                    row.clients[clientId][
                                        reportData.startingMonth + 2
                                    ].userCount
                                }
                            </TableCell>
                        </TableRow>
                    ))}
            </React.Fragment>
        );
    };

    render() {
        const { reportData, t } = this.props;

        const { data } = this.state;

        const tableHeadersApplicationBreakdown =
            this.headersApplicationBreakdown({
                reportDataMonths: reportData.months,
                t
            });

        return (
            <Box mt="32px" width="100%" className="break-before">
                <Typography variant="h5">
                    {t(
                        'Reports.PartnerApplicationOverviewReport.application_breakdown_by_user_count'
                    )}
                </Typography>
                <Typography paragraph>
                    {t(
                        'Reports.partnerApplicationOverviewReport.applicationBreakdown.description'
                    )}
                    <br />
                    {t(
                        'Reports.partnerApplicationOverviewReport.applicationBreakdown.description.detail'
                    )}
                </Typography>
                <ManagedTable
                    headers={tableHeadersApplicationBreakdown}
                    manageRows
                    renderBody={this.renderRow}
                    renderBodyKey={null}
                    renderFooter={false}
                    rows={data}
                    rowsPerPage={-1}
                    searchEnabled={false}
                    sortDirection="DESC"
                    sortFieldType="number"
                    sortKey="quarter"
                />
            </Box>
        );
    }
}

const UsageOverview = (props) => {
    const theme = useTheme();
    const { renderingScheduledReport, reportData, t } = props;

    const usageReportKeys = getUsageReportKeys(reportData.data);

    const barData = getBarData({
        data: reportData,
        variable: 'applicationUsage'
    });

    return (
        <Box mt="32px" minHeight="500px" width="100%">
            <Box
                style={{
                    height: '400px'
                }}
                data-trackid="partnerAppOverviewReportUsageOverviewBarChartGridItemNoRenderingScheduleReport"
            >
                <Typography variant="h5">
                    {t(
                        'Reports.PartnerApplicationOverviewReport.application_usage_by_category_overview'
                    )}
                </Typography>
                <Typography paragraph>
                    {t(
                        'Reports.partnerApplicationOverviewReport.overviewBreakdown.description'
                    )}
                </Typography>
                <ResponsiveContainer height="100%" minHeight={400} width="100%">
                    <BarChart data={barData} margin={{ left: 20 }}>
                        <CartesianGrid
                            stroke={theme.palette.chart.axis}
                            strokeDasharray="3 3"
                        />
                        <XAxis dataKey="name" />
                        <YAxis tickFormatter={hmsFormatter} />
                        <Tooltip />
                        <Legend
                            wrapperStyle={{
                                bottom: 0,
                                left: 0,
                                width: '100%'
                            }}
                        />
                        {usageReportKeys.map((row, index) => (
                            <Bar
                                isAnimationActive={!renderingScheduledReport}
                                key={`iuo_${row}`}
                                dataKey={row}
                                stackId="a"
                                fill={
                                    theme.palette.graph.colors[
                                        index %
                                            theme.palette.graph.colors.length
                                    ]
                                }
                            />
                        ))}
                    </BarChart>
                </ResponsiveContainer>
            </Box>
        </Box>
    );
};

const headersUsageOverviewBreakdown = (t) => [
    {
        align: 'left',
        field: 'riubCategory',
        renderHeader: <strong>{t('Common.category')}</strong>,
        width: '50%'
    },
    {
        align: 'right',
        field: 'riubUsers',
        renderHeader: <strong>{t('Common.usage')}</strong>,
        width: '25%'
    },
    {
        align: 'right',
        field: 'riubPercentage',
        renderHeader: <strong>{t('Common.percentage')}</strong>,
        width: '25%'
    }
];

const UsageOverviewBreakdown = (props) => {
    const { renderingScheduledReport, reportData, t } = props;

    const tableHeadersUsageOverviewBreakdown = useMemo(
        () => headersUsageOverviewBreakdown(t),
        [t]
    );

    return (
        <Box mt="32px" width="100%">
            <Typography variant="h5">
                {t(
                    'Reports.PartnerApplicationOverviewReport.application_usage_by_category_breakdown'
                )}
            </Typography>
            <Typography paragraph>
                {t(
                    'Reports.partnerApplicationOverviewReport.overviewBreakdown.description'
                )}
            </Typography>
            <ManagedTable
                headers={tableHeadersUsageOverviewBreakdown}
                renderBody={(row) => (
                    <>
                        <TableCell>{row.categoryName}</TableCell>
                        <TableCell align="right">
                            {hmsFormatter(row.applicationUsage)}
                        </TableCell>
                        <TableCell align="right">
                            {`${row.percentage} %`}
                        </TableCell>
                    </>
                )}
                renderBodyKey={null}
                renderFooter={!renderingScheduledReport}
                rows={reportData.data}
                rowsPerPage={-1}
                searchEnabled={false}
                sortDirection="DESC"
                sortFieldType="number"
                sortKey="applicationCount"
            />
        </Box>
    );
};

class ApplicationUsageBreakdown extends Component {
    constructor(props) {
        super(props);
        this.state = {
            data: []
        };
    }

    componentDidMount() {
        const { reportData } = this.props;
        this.setState({ data: reportData.data });
    }

    setOpen(row) {
        const { data } = this.state;
        this.setState({
            data: data.map((a) => ({
                ...a,
                open: a.applicationId === row.applicationId ? !a.open : a.open
            }))
        });
    }

    // eslint-disable-next-line class-methods-use-this
    headers = ({ reportDataMonths, t }) => {
        const headers = [];
        headers.push({
            align: 'left',
            field: 'chevron',
            renderHeader: <strong>&nbsp;</strong>,
            width: '5%'
        });
        headers.push({
            align: 'left',
            field: 'application',
            renderHeader: <strong>{t('Common.application')}</strong>,
            width: '47%'
        });
        headers.push({
            align: 'left',
            field: 'overviewSubCategory',
            renderHeader: <strong>{t('Common.quarter')}</strong>,
            width: '12%'
        });
        headers.push({
            align: 'left',
            field: 'overviewRisk',
            renderHeader: <strong>{reportDataMonths[0]}</strong>,
            width: '12%'
        });
        headers.push({
            align: 'left',
            field: 'overviewBusinessOwner',
            renderHeader: <strong>{reportDataMonths[1]}</strong>,
            width: '12%'
        });
        headers.push({
            align: 'left',
            field: 'overviewStatus',
            renderHeader: <strong>{reportDataMonths[2]}</strong>,
            width: '12%'
        });

        return headers;
    };

    renderRow = (row) => {
        const { match, renderingScheduledReport, reportData } = this.props;

        const { partnerId } = match.params;
        const basePartnerRoute = Route.buildBasePartnerRoute({
            partnerId
        });
        return (
            <React.Fragment key={`usage_app__${row.applicationId}`}>
                <TableRow
                    sx={{
                        backgroundColor: 'table.rowBackground'
                    }}
                >
                    <Application
                        data={reportData.applicationMap[row.applicationId]}
                        nameLinkTo={`${basePartnerRoute}/applicationSettings`}
                    >
                        <TableCell>
                            {!renderingScheduledReport && (
                                <IconButton
                                    data-trackid={
                                        row.open
                                            ? 'reports_partnerApplicationOverview_appUsageBreakdown_collapseRow'
                                            : 'reports_partnerApplicationOverview_appUSageBreakdown_expandRow'
                                    }
                                    aria-label="expand row"
                                    size="small"
                                    onClick={() => {
                                        this.setOpen(row);
                                    }}
                                    sx={{ color: 'icon.textColor' }}
                                >
                                    {row.open ? (
                                        <KeyboardArrowUp />
                                    ) : (
                                        <KeyboardArrowDown />
                                    )}
                                </IconButton>
                            )}
                        </TableCell>
                        <TableCell>
                            <ApplicationNameLinkCell nameNoTruncate />
                        </TableCell>
                        <TableCell>{hmsFormatter(row.quarter)}</TableCell>
                        <TableCell>
                            {hmsFormatter(row[reportData.startingMonth])}
                        </TableCell>
                        <TableCell>
                            {hmsFormatter(row[reportData.startingMonth + 1])}
                        </TableCell>
                        <TableCell>
                            {hmsFormatter(row[reportData.startingMonth + 2])}
                        </TableCell>
                    </Application>
                </TableRow>
                {row.open &&
                    Object.keys(row.clients).map((clientId) => (
                        <TableRow
                            key={`ubreakdown_${clientId}`}
                            sx={{
                                backgroundColor: 'table.subtableBackground'
                            }}
                        >
                            <TableCell>
                                <strong>&nbsp;</strong>
                            </TableCell>
                            <TableCell>
                                {reportData.tenantLinkMap[clientId].tenant.name}
                            </TableCell>
                            <TableCell>
                                {hmsFormatter(
                                    row.clients[clientId].quarter.usage
                                )}
                            </TableCell>
                            <TableCell>
                                {hmsFormatter(
                                    row.clients[clientId][
                                        reportData.startingMonth
                                    ].usage
                                )}
                            </TableCell>
                            <TableCell>
                                {hmsFormatter(
                                    row.clients[clientId][
                                        reportData.startingMonth + 1
                                    ].usage
                                )}
                            </TableCell>
                            <TableCell>
                                {hmsFormatter(
                                    row.clients[clientId][
                                        reportData.startingMonth + 2
                                    ].usage
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
            </React.Fragment>
        );
    };

    render() {
        const { reportData, t } = this.props;

        const { data } = this.state;

        const tableHeaders = this.headers({
            reportDataMonths: reportData.months,
            t
        });

        return (
            <Box width="100%" className="break-before">
                <Typography variant="h5">
                    {t(
                        'Reports.PartnerApplicationOverviewReport.application_breakdown_by_user_count'
                    )}
                </Typography>
                <Typography paragraph>
                    {t(
                        'Reports.partnerApplicationOverviewReport.applicationBreakdown.description'
                    )}
                    <br />
                    {t(
                        'Reports.partnerApplicationOverviewReport.applicationBreakdown.description.detail'
                    )}
                </Typography>

                <ManagedTable
                    headers={tableHeaders}
                    manageRows
                    renderBody={this.renderRow}
                    renderBodyKey={null}
                    renderFooter={false}
                    rows={data}
                    rowsPerPage={-1}
                    searchEnabled={false}
                    sortDirection="DESC"
                    sortFieldType="number"
                    sortKey="quarter"
                />
            </Box>
        );
    }
}

export default withRouter(withTranslation()(PartnerApplicationOverviewReports));
